/* ============================================================
   Fragrance Finder Quiz — fragrance-quiz.js
   Amara Parfums | No external dependencies
   v2.0 — 5-step redesign with image tiles, weighted scoring
   ============================================================ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────
     CONFIG
  ────────────────────────────────────────────────────────── */
  const REQUIRE_EMAIL    = false;   // email captured post-results, not as a gate
  const ENABLE_AB_TEST   = false;
  const KLAVIYO_PK       = 'XkCpvv';
  const KLAVIYO_LIST_ID  = 'UhjUFX';

  /* ──────────────────────────────────────────────────────────
     LOCALSTORAGE KEYS
  ────────────────────────────────────────────────────────── */
  const LS = {
    ANSWERS: 'amara_quiz_answers',
    VARIANT: 'amara_quiz_variant',
    EMAIL:   'amara_email_captured',
    RESULTS: 'amara_quiz_results',
  };

  /* ──────────────────────────────────────────────────────────
     STEP DEFINITIONS  (5 steps)
  ────────────────────────────────────────────────────────── */
  const STEPS = [
    {
      id: 1,
      key: 'gender',
      title: 'Who is this for?',
      type: 'single',
      required: true,
      skippable: false,
      renderAs: 'tiles',
      gridClass: 'quiz-step__options--tiles',
      options: [
        { value: 'f',        label: 'For me',           sub: 'she / her'   },
        { value: 'm',        label: 'For me',           sub: 'he / him'    },
        { value: 'both',     label: 'I wear both',      sub: ''            },
        { value: 'surprise', label: 'For someone else', sub: 'surprise me' },
      ],
    },
    {
      id: 2,
      key: 'scentFamilies',
      title: 'Discover your scent world',
      subtitle: 'Choose up to 2 that draw you in',
      type: 'multi',
      required: true,
      skippable: false,
      max: 2,
      renderAs: 'image-tile',
      options: [
        {
          value: 'fresh',
          label: 'Fresh & Clean',
          tagline: 'Ocean · Citrus · Clean',
          description: 'Think ocean air, citrus rind and cut grass. Light, energising and crowd-pleasing — the go-to for everyday wear.',
          imageKey: 'scent-fresh',
        },
        {
          value: 'floral',
          label: 'Floral',
          tagline: 'Rose · Jasmine · Petals',
          description: 'Rose gardens, jasmine at dusk and powdery soft petals. Romantic, feminine and timelessly beautiful.',
          imageKey: 'scent-floral',
        },
        {
          value: 'woody',
          label: 'Woody & Earthy',
          tagline: 'Cedar · Leather · Earth',
          description: 'Forest floors, warm cedar and supple leather. Grounded, sophisticated and unmistakably confident.',
          imageKey: 'scent-woody',
        },
        {
          value: 'oriental',
          label: 'Oriental & Spicy',
          tagline: 'Oud · Amber · Spice',
          description: 'Spice markets, smouldering amber resin and oud smoke. Rich, opulent and deeply captivating.',
          imageKey: 'scent-oriental',
        },
        {
          value: 'gourmand',
          label: 'Gourmand & Sweet',
          tagline: 'Vanilla · Caramel · Warmth',
          description: 'Warm vanilla cream, dark caramel and fresh-baked pastry. Comforting, indulgent and irresistibly sweet.',
          imageKey: 'scent-gourmand',
        },
        {
          value: 'fruity',
          label: 'Fruity & Playful',
          tagline: 'Peach · Berry · Zest',
          description: 'Sun-warmed peach, tropical zest and juicy berry. Playful, bright and full of energy.',
          imageKey: 'scent-fruity',
        },
      ],
    },
    {
      id: 3,
      key: 'vibe',
      title: 'Pick your vibe',
      subtitle: 'Choose up to 2 moments that speak to you',
      type: 'multi',
      required: true,
      skippable: false,
      max: 2,
      renderAs: 'image-tile',
      options: [
        { value: 'morning-ritual', label: 'Morning ritual', tagline: 'Fresh start · Everyday', imageKey: 'vibe-morning'   },
        { value: 'board-room',     label: 'Board room',     tagline: 'Polished · Confident',   imageKey: 'vibe-boardroom' },
        { value: 'date-night',     label: 'Date night',     tagline: 'Alluring · Romantic',    imageKey: 'vibe-datenight' },
        { value: 'weekend-escape', label: 'Weekend escape', tagline: 'Relaxed · Effortless',   imageKey: 'vibe-weekend'   },
        { value: 'night-out',      label: 'Night out',      tagline: 'Bold · Festive',         imageKey: 'vibe-nightout'  },
        { value: 'sunday-home',    label: 'Sunday at home', tagline: 'Cosy · Comfortable',     imageKey: 'vibe-sunday'    },
      ],
    },
    {
      id: 4,
      key: 'feel',
      title: 'How do you want it to feel?',
      subtitle: 'How present should your scent be?',
      type: 'single',
      required: true,
      skippable: false,
      renderAs: 'image-tile',
      options: [
        { value: 'whisper',    label: 'A soft whisper',        tagline: 'Subtle — just for you',                 imageKey: 'feel-whisper'    },
        { value: 'effortless', label: 'Present but effortless', tagline: 'Noticeable — lasts most of the day',   imageKey: 'feel-effortless' },
        { value: 'commanding', label: 'Commanding',             tagline: 'People notice — stays all day',        imageKey: 'feel-commanding' },
        { value: 'statement',  label: 'Full statement',         tagline: 'Bold — stays until bedtime',           imageKey: 'feel-statement'  },
      ],
    },
    {
      id: 5,
      key: 'note',
      title: 'A note you\'re curious about?',
      subtitle: 'Choose one that interests you — or skip if unsure',
      type: 'single',
      required: false,
      skippable: true,
      renderAs: 'image-tile',
      options: [
        { value: 'bergamot',   label: 'Citrus',  tagline: 'Bergamot · Lemon · Zest',         imageKey: 'note-citrus'  },
        { value: 'rose',       label: 'Floral',  tagline: 'Rose · Jasmine · Neroli',          imageKey: 'note-floral'  },
        { value: 'cardamom',   label: 'Spice',   tagline: 'Cardamom · Pepper · Ginger',       imageKey: 'note-spice'   },
        { value: 'sandalwood', label: 'Woods',   tagline: 'Sandalwood · Cedar · Vetiver',      imageKey: 'note-woods'   },
        { value: 'vanilla',    label: 'Sweet',   tagline: 'Vanilla · Tonka · Caramel',        imageKey: 'note-sweet'   },
        { value: 'musk',       label: 'Musk',    tagline: 'Musk · Amber · Skin',              imageKey: 'note-musk'    },
      ],
    },
  ];

  /* ──────────────────────────────────────────────────────────
     SCORING VALUE MAPS
  ────────────────────────────────────────────────────────── */

  const GENDER_MATCH = {
    f:        ['female', 'unisex'],
    m:        ['male', 'unisex'],
    both:     ['female', 'male', 'unisex'],
    surprise: [],
  };

  // Maps quiz scent family choice → product scent tag values
  const SCENT_FAMILY_MAP = {
    fresh:    ['fresh', 'citrus', 'aquatic', 'aromatic', 'fresh-linen'],
    floral:   ['floral', 'rose'],
    woody:    ['woody', 'sandalwood', 'leather', 'musk'],
    oriental: ['oriental', 'oud', 'amber', 'spicy'],
    gourmand: ['gourmand', 'vanilla'],
    fruity:   ['fruity'],
  };

  // Maps vibe answer → occasions + moods used in scoring
  const VIBE_MAP = {
    'morning-ritual': { occasions: ['everyday', 'casual'],  moods: ['fresh', 'cool']              },
    'board-room':     { occasions: ['formal'],              moods: ['confident']                   },
    'date-night':     { occasions: ['date-night'],          moods: ['sensual']                     },
    'weekend-escape': { occasions: ['casual'],              moods: ['cool']                        },
    'night-out':      { occasions: ['nightlife'],           moods: ['confident', 'sensual']        },
    'sunday-home':    { occasions: ['everyday', 'casual'],  moods: ['warm']                        },
  };

  // Maps feel answer → intensity + sillage + longevity for scoring
  const FEEL_MAP = {
    'whisper':     { intensities: ['soft', 'skin-scent'], sillage: 'skin scent',     longevity: 'moderate'     },
    'effortless':  { intensities: ['significant'],        sillage: 'moderate',       longevity: 'long-lasting' },
    'commanding':  { intensities: ['significant'],        sillage: 'moderate',       longevity: 'long-lasting' },
    'statement':   { intensities: ['statement'],          sillage: 'projection beast', longevity: 'eternal'    },
  };

  // Maps mood values → quiz_projection product field (kept for backward compatibility)
  const MOOD_TO_PROJECTION_MAP = {
    confident:  'bold & seductive',
    sensual:    'bold & seductive',
    cool:       'fresh & clean',
    warm:       'warm & cosy',
    fresh:      'fresh & clean',
    mysterious: 'dark & mysterious',
  };

  const OCCASION_VALUE_MAP = {
    'everyday':   'everyday',
    'formal':     'formal',
    'date-night': 'date',
    'casual':     'casual',
    'nightlife':  'special-occasion',
  };

  const OCCASION_LABELS = {
    everyday:    'Everyday',
    formal:      'Formal',
    'date-night': 'Date night',
    casual:      'Casual',
    nightlife:   'Nightlife',
  };

  const FAMILY_LABELS = {
    fresh:    'Fresh & Clean',
    floral:   'Floral',
    woody:    'Woody',
    oriental: 'Oriental & Spicy',
    gourmand: 'Gourmand',
    fruity:   'Fruity',
  };

  /* ──────────────────────────────────────────────────────────
     STATE
  ────────────────────────────────────────────────────────── */
  let state = {
    currentStep: 0,
    answers: {},
    skippedSteps: new Set(),
    products: null,
    results: null,
    variant: null,
    phase: 'quiz',
  };

  /* ──────────────────────────────────────────────────────────
     DOM REFS
  ────────────────────────────────────────────────────────── */
  let DOM = {};

  /* ──────────────────────────────────────────────────────────
     UTILITY HELPERS
  ────────────────────────────────────────────────────────── */
  function $(id) { return document.getElementById(id); }

  function normalizeStr(str) {
    return (str || '').toLowerCase().trim();
  }

  function normalizeList(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(normalizeStr).filter(Boolean);
    return String(val).split(',').map(normalizeStr).filter(Boolean);
  }

  function formatPrice(cents) {
    if (cents == null) return '';
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function toTitleCase(str) {
    return String(str || '').replace(/\b\w/g, c => c.toUpperCase());
  }

  /* ──────────────────────────────────────────────────────────
     LOCALSTORAGE HELPERS
  ────────────────────────────────────────────────────────── */
  function lsGet(key) {
    try { return JSON.parse(localStorage.getItem(key)); }
    catch(e) { return null; }
  }

  function lsSet(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); }
    catch(e) {}
  }

  function lsRemove(key) {
    try { localStorage.removeItem(key); }
    catch(e) {}
  }

  /* ──────────────────────────────────────────────────────────
     A/B TESTING
  ────────────────────────────────────────────────────────── */
  function initABTest() {
    if (!ENABLE_AB_TEST) { state.variant = 'A'; return; }
    let variant = lsGet(LS.VARIANT);
    if (!variant) {
      variant = Math.random() < 0.5 ? 'A' : 'B';
      lsSet(LS.VARIANT, variant);
    }
    state.variant = variant;
  }

  /* ──────────────────────────────────────────────────────────
     GA4 TRACKING
  ────────────────────────────────────────────────────────── */
  function trackEvent(eventName, params) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, Object.assign({ quiz_variant: state.variant }, params));
    }
  }

  /* ──────────────────────────────────────────────────────────
     URL ENCODING / DECODING  (updated for v2 answer keys)
  ────────────────────────────────────────────────────────── */
  function encodeAnswersToURL(answers) {
    const p = new URLSearchParams();
    if (answers.gender)
      p.set('g', answers.gender);
    if (answers.scentFamilies && answers.scentFamilies.length)
      p.set('sf', answers.scentFamilies.join(','));
    if (answers.vibe && answers.vibe.length)
      p.set('vb', answers.vibe.join(','));
    if (answers.feel)
      p.set('fl', answers.feel);
    if (answers.note && answers.note !== 'no-preference')
      p.set('note', encodeURIComponent(answers.note));
    return p.toString();
  }

  function decodeURLToAnswers() {
    const p = new URLSearchParams(window.location.search);
    if (!p.has('g')) return null;
    const answers = {};
    const skipped = new Set();
    answers.gender        = p.get('g') || '';
    answers.scentFamilies = p.has('sf') ? p.get('sf').split(',').filter(Boolean) : [];
    answers.vibe          = p.has('vb') ? p.get('vb').split(',').filter(Boolean) : [];
    answers.feel          = p.get('fl') || '';
    answers.note          = p.has('note') ? decodeURIComponent(p.get('note')) : 'no-preference';
    if (!p.has('note')) skipped.add(5);
    return { answers, skipped };
  }

  function getShareableURL() {
    const base = window.location.origin + window.location.pathname;
    return base + '?' + encodeAnswersToURL(state.answers);
  }

  /* ──────────────────────────────────────────────────────────
     PROGRESS DOTS
  ────────────────────────────────────────────────────────── */
  function updateProgress(stepIndex) {
    const total  = STEPS.length;
    const stepNum = stepIndex + 1;
    const step   = STEPS[stepIndex];

    if (DOM.progressLabel) {
      DOM.progressLabel.textContent = 'Step ' + stepNum + ' of ' + total;
    }
    if (DOM.progressbar) {
      DOM.progressbar.setAttribute('aria-valuenow', stepNum);
      DOM.progressbar.setAttribute('aria-valuetext', 'Step ' + stepNum + ' of ' + total + ': ' + step.title);
    }

    // Update dot states
    document.querySelectorAll('.quiz-progress__dot').forEach(dot => {
      const dotStep = parseInt(dot.dataset.step, 10);
      dot.classList.remove('is-active', 'is-completed');
      if (dotStep === stepNum)      dot.classList.add('is-active');
      else if (dotStep < stepNum)   dot.classList.add('is-completed');
    });

    // Keep fill ref for any code still referencing it
    if (DOM.progressFill) DOM.progressFill.style.width = (stepNum / total * 100) + '%';
  }

  /* ──────────────────────────────────────────────────────────
     STEP RENDERING
  ────────────────────────────────────────────────────────── */
  function renderStep(stepIndex) {
    const step    = STEPS[stepIndex];
    const current = state.answers[step.key];
    const animOn  = (window.AMARA_QUIZ && window.AMARA_QUIZ.animationsOn);

    updateProgress(stepIndex);

    const el = document.createElement('div');
    el.className = 'quiz-step quiz-step--enter' + (animOn ? ' scroll-trigger animate--slide-in' : '');
    el.id        = 'quiz-step-' + step.id;
    el.setAttribute('data-step', stepIndex);

    // Header
    el.innerHTML = [
      '<div class="quiz-step__header">',
        '<h2 class="quiz-step__title">' + escapeHTML(step.title) + '</h2>',
        step.subtitle ? '<p class="quiz-step__subtitle">' + escapeHTML(step.subtitle) + '</p>' : '',
      '</div>',
    ].join('');

    // Options area
    if (step.renderAs === 'image-tile') {
      el.innerHTML += renderImageTilesHTML(step, current);
    } else if (step.type === 'single' || step.type === 'multi') {
      el.innerHTML += renderTilesHTML(step, current);
    } else if (step.type === 'dropdown') {
      el.innerHTML += renderDropdownHTML(step, current);
    }

    // Footer
    const isSelected = hasSelection(step, current);
    el.innerHTML += [
      '<div class="quiz-step__footer">',
        '<button ',
          'class="button button--primary button--black quiz-continue-btn" ',
          'id="quiz-continue-btn" ',
          (!isSelected && step.required && step.type !== 'dropdown' ? 'disabled' : ''),
          ' aria-label="Continue to next step"',
        '>',
          'Continue',
        '</button>',
        step.skippable
          ? '<button type="button" class="quiz-skip-link" id="quiz-skip-btn">Skip this step</button>'
          : '',
      '</div>',
    ].join('');

    // Swap in with animation
    const container = DOM.stepsContainer;
    const existing  = container.firstElementChild;

    if (existing) {
      existing.classList.add('quiz-step--exit');
      existing.addEventListener('animationend', () => {
        if (container.contains(existing)) container.removeChild(existing);
      }, { once: true });
    }

    container.appendChild(el);

    bindStepEvents(el, step, stepIndex);

    if (step.type === 'multi') {
      updateSelectionCount(el, step, current || []);
    }

    scrollToQuiz();
  }

  function hasSelection(step, current) {
    if (step.type === 'single')   return !!current;
    if (step.type === 'multi')    return Array.isArray(current) && current.length > 0;
    if (step.type === 'dropdown') return true;
    return false;
  }

  /* ── Standard text tiles (Step 1 only) ── */
  function renderTilesHTML(step, current) {
    const selected = step.type === 'multi' ? (current || []) : [];

    const tiles = step.options.map(opt => {
      const isSelected = step.type === 'single'
        ? current === opt.value
        : selected.includes(opt.value);

      return [
        '<button ',
          'type="button" ',
          'class="quiz-option-tile' + (isSelected ? ' is-selected' : '') + '" ',
          'data-value="' + escapeHTML(opt.value) + '" ',
          'aria-pressed="' + isSelected + '"',
        '>',
          '<span class="quiz-option-tile__label">' + escapeHTML(opt.label) + '</span>',
          opt.sub ? '<span class="quiz-option-tile__sub">' + escapeHTML(opt.sub) + '</span>' : '',
          '<span class="quiz-option-tile__check" aria-hidden="true"></span>',
        '</button>',
      ].join('');
    }).join('');

    const maxClass = (step.type === 'multi' && step.max && selected.length >= step.max)
      ? ' quiz-options-max-reached'
      : '';

    return [
      '<div class="quiz-step__options ' + (step.gridClass || '') + maxClass + '" id="quiz-options-grid">',
        tiles,
      '</div>',
      step.type === 'multi'
        ? '<p class="quiz-selection-count" id="quiz-selection-count" aria-live="polite"></p>'
        : '',
    ].join('');
  }

  /* ── Image tiles (Steps 2–5) ── */
  function renderImageTilesHTML(step, current) {
    const selected      = step.type === 'multi' ? (current || []) : [];
    const quizImages    = (window.AMARA_QUIZ && window.AMARA_QUIZ.images) || {};
    const hasEducation  = step.options.some(o => o.description);

    const tiles = step.options.map(opt => {
      const isSelected = step.type === 'single'
        ? current === opt.value
        : selected.includes(opt.value);

      const imageSrc = opt.imageKey ? (quizImages[opt.imageKey] || '') : '';

      const imageHTML = imageSrc
        ? '<div class="quiz-tile-image-wrap"><img src="' + escapeHTML(imageSrc) + '" alt="" loading="lazy" aria-hidden="true" class="quiz-tile-image"></div>'
        : '<div class="quiz-tile-image-wrap quiz-tile-image-wrap--placeholder quiz-tile-placeholder--' + escapeHTML(opt.value.replace(/[^a-z0-9-]/g, '')) + '" aria-hidden="true"></div>';

      const expandHTML = opt.description
        ? [
            '<button type="button" class="quiz-tile-expand-btn" aria-expanded="false" aria-label="What does ' + escapeHTML(opt.label) + ' smell like?">',
              'What does this smell like?',
            '</button>',
            '<div class="quiz-tile-expand-panel" hidden>',
              '<p class="quiz-tile-expand-text">' + escapeHTML(opt.description) + '</p>',
            '</div>',
          ].join('')
        : '';

      return [
        '<div ',
          'class="quiz-option-tile quiz-option-tile--image' + (isSelected ? ' is-selected' : '') + '" ',
          'data-value="' + escapeHTML(opt.value) + '" ',
          'role="button" ',
          'tabindex="0" ',
          'aria-pressed="' + isSelected + '"',
        '>',
          imageHTML,
          '<div class="quiz-tile-content">',
            '<span class="quiz-tile-label">' + escapeHTML(opt.label) + '</span>',
            opt.tagline ? '<span class="quiz-tile-tagline">' + escapeHTML(opt.tagline) + '</span>' : '',
          '</div>',
          expandHTML,
          '<span class="quiz-option-tile__check" aria-hidden="true"></span>',
        '</div>',
      ].join('');
    }).join('');

    const maxClass = (step.type === 'multi' && step.max && selected.length >= step.max)
      ? ' quiz-options-max-reached'
      : '';

    return [
      '<div class="quiz-step__options quiz-step__options--image-tiles' + (hasEducation ? ' quiz-step__options--with-education' : '') + maxClass + '" id="quiz-options-grid">',
        tiles,
      '</div>',
      step.type === 'multi'
        ? '<p class="quiz-selection-count" id="quiz-selection-count" aria-live="polite"></p>'
        : '',
    ].join('');
  }

  /* ── Selection count ── */
  function updateSelectionCount(stepEl, step, selected) {
    const el = stepEl.querySelector('#quiz-selection-count');
    if (!el) return;
    const count = selected.length;
    const max   = step.max;
    if (!count) {
      el.textContent = '';
      el.classList.remove('is-complete');
    } else if (max && count >= max) {
      el.textContent = count + ' of ' + max + ' selected';
      el.classList.add('is-complete');
    } else if (max) {
      el.textContent = count + ' of ' + max + ' selected';
      el.classList.remove('is-complete');
    } else {
      el.textContent = count + ' selected';
    }
  }

  /* ──────────────────────────────────────────────────────────
     STEP EVENT BINDING
  ────────────────────────────────────────────────────────── */
  function bindStepEvents(el, step, stepIndex) {
    const continueBtn = el.querySelector('#quiz-continue-btn');
    const skipBtn     = el.querySelector('#quiz-skip-btn');

    /* Single-select (tiles or image tiles) */
    if (step.type === 'single') {
      el.querySelectorAll('.quiz-option-tile').forEach(tile => {
        tile.addEventListener('click', (e) => {
          if (e.target.closest('.quiz-tile-expand-btn')) return; // handled separately
          el.querySelectorAll('.quiz-option-tile').forEach(t => {
            t.classList.remove('is-selected');
            t.setAttribute('aria-pressed', 'false');
          });
          tile.classList.add('is-selected');
          tile.setAttribute('aria-pressed', 'true');
          state.answers[step.key] = tile.dataset.value;
          lsSet(LS.ANSWERS, { answers: state.answers, skippedSteps: [...state.skippedSteps] });
          if (continueBtn) continueBtn.disabled = false;
        });
      });
    }

    /* Multi-select (tiles or image tiles) */
    if (step.type === 'multi') {
      if (!Array.isArray(state.answers[step.key])) {
        state.answers[step.key] = [];
      }

      el.querySelectorAll('.quiz-option-tile').forEach(tile => {
        tile.addEventListener('click', (e) => {
          if (e.target.closest('.quiz-tile-expand-btn')) return;
          const val  = tile.dataset.value;
          const arr  = state.answers[step.key];
          const idx  = arr.indexOf(val);
          const grid = el.querySelector('#quiz-options-grid');

          if (idx > -1) {
            arr.splice(idx, 1);
            tile.classList.remove('is-selected');
            tile.setAttribute('aria-pressed', 'false');
          } else {
            if (step.max && arr.length >= step.max) return;
            arr.push(val);
            tile.classList.add('is-selected');
            tile.setAttribute('aria-pressed', 'true');
          }

          if (grid) {
            if (step.max && arr.length >= step.max) grid.classList.add('quiz-options-max-reached');
            else grid.classList.remove('quiz-options-max-reached');
          }

          updateSelectionCount(el, step, arr);
          lsSet(LS.ANSWERS, { answers: state.answers, skippedSteps: [...state.skippedSteps] });
          if (continueBtn) continueBtn.disabled = arr.length === 0;
        });
      });
    }

    /* Education expand buttons (Step 2) */
    el.querySelectorAll('.quiz-tile-expand-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const panel  = btn.parentElement.querySelector('.quiz-tile-expand-panel');
        if (!panel) return;
        const isOpen = !panel.hidden;
        panel.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
        btn.textContent = isOpen ? 'What does this smell like?' : 'Close \u2715';
      });
    });

    /* Dropdown */
    if (step.type === 'dropdown') {
      const select = el.querySelector('#quiz-note-select');
      if (select) {
        select.addEventListener('change', () => {
          state.answers[step.key] = select.value;
          lsSet(LS.ANSWERS, { answers: state.answers, skippedSteps: [...state.skippedSteps] });
        });
        if (!state.answers[step.key]) state.answers[step.key] = 'no-preference';
      }
    }

    /* Continue button */
    if (continueBtn) {
      continueBtn.addEventListener('click', () => advanceStep(stepIndex));
    }

    /* Skip button */
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        state.skippedSteps.add(step.id);
        lsSet(LS.ANSWERS, { answers: state.answers, skippedSteps: [...state.skippedSteps] });
        advanceStep(stepIndex, true);
      });
    }

    /* Keyboard: Enter/Space on focused tile div or button */
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.target.closest('.quiz-tile-expand-btn')) return; // button handles its own keypress
        const tile = e.target.closest('.quiz-option-tile');
        if (tile) { e.preventDefault(); tile.click(); }
      }
    });
  }

  /* ──────────────────────────────────────────────────────────
     NAVIGATION
  ────────────────────────────────────────────────────────── */
  function advanceStep(stepIndex, skipped) {
    const step = STEPS[stepIndex];
    trackEvent('quiz_step_complete', { step: step.id, step_key: step.key, skipped: !!skipped });

    const nextIndex = stepIndex + 1;

    if (nextIndex >= STEPS.length) {
      finishQuiz();
      return;
    }

    state.currentStep = nextIndex;
    renderStep(nextIndex);
  }

  function scrollToQuiz() {
    const quiz = $('fragrance-quiz');
    if (!quiz) return;
    const rect = quiz.getBoundingClientRect();
    if (rect.top < 0 || rect.top > 80) {
      window.scrollTo({ top: window.scrollY + rect.top - 80, behavior: 'smooth' });
    }
  }

  /* ──────────────────────────────────────────────────────────
     SCORING ENGINE
  ────────────────────────────────────────────────────────── */
  function computeMaxScore(answers) {
    let max = 0;

    // Gender
    if (answers.gender !== 'surprise') max += 3;

    // Scent families (weighted or binary, up to 2 families × (2.5 base + 1.5 bonus))
    const famCount = (answers.scentFamilies || []).length;
    max += famCount * 2.5;
    max += famCount * 1.5;

    // Favourite note
    if (answers.note && answers.note !== 'no-preference') max += 3;

    // Vibe → occasions (up to 3 unique occasions × 2pts, capped at 6) + mood (1.5)
    const vibeValues = answers.vibe || [];
    let allOccasions = [];
    vibeValues.forEach(v => {
      const mapped = VIBE_MAP[v];
      if (mapped) allOccasions = allOccasions.concat(mapped.occasions);
    });
    const uniqueOccasions = [...new Set(allOccasions)];
    max += Math.min(uniqueOccasions.length * 2, 6);
    max += 1.5; // mood

    // Feel → intensity (2) + sillage (2) + longevity (1.5)
    if (answers.feel) {
      max += 2;
      max += 2;
      max += 1.5;
    }

    return max || 1;
  }

  function scoreProduct(product, answers, maxScore) {
    let score = 0;
    const reasons = [];

    // ── Gender ──
    if (answers.gender !== 'surprise') {
      const pGender     = normalizeStr(product.gender);
      const validGenders = GENDER_MATCH[answers.gender] || [];
      if (validGenders.includes(pGender)) {
        score += 3;
        reasons.push({ label: pGender === 'unisex' ? 'Unisex' : answers.gender === 'f' ? 'For her' : 'For him', points: 3 });
      }
    }

    // ── Scent families (weighted accord scoring with binary fallback) ──
    const accordWeights = product.accord_weights || {};
    const hasWeights    = Object.keys(accordWeights).length > 0;
    const pScents       = product.scent || [];

    (answers.scentFamilies || []).forEach(family => {
      const familyTags = SCENT_FAMILY_MAP[family] || [family];

      if (hasWeights) {
        // Weighted: find the highest weight among tags for this family
        let maxWeight = 0;
        let totalWeight = 0;
        familyTags.forEach(tag => {
          const w = accordWeights[tag] || 0;
          if (w > maxWeight) maxWeight = w;
          totalWeight += w;
        });
        if (maxWeight > 0) {
          const familyScore = (maxWeight / 100) * 2.5;
          const bonus       = Math.min((totalWeight / 100) * 0.75, 1.5);
          score += familyScore + bonus;
          reasons.push({ label: FAMILY_LABELS[family] || family, points: familyScore });
        }
      } else {
        // Fallback: binary scent tag presence
        const matched = pScents.filter(ps => familyTags.includes(normalizeStr(ps)));
        if (matched.length > 0) {
          score += 2.5;
          const accordBonus = Math.min(matched.length * 0.5, 1.5);
          score += accordBonus;
          reasons.push({ label: FAMILY_LABELS[family] || family, points: 2.5 });
        }
      }
    });

    // ── Favourite note ──
    const note = normalizeStr(answers.note);
    if (note && note !== 'no-preference') {
      const topNotes  = normalizeList(product.top_notes);
      const midNotes  = normalizeList(product.middle_notes);
      const baseNotes = normalizeList(product.base_notes);
      if (topNotes.some(n => n.includes(note) || note.includes(n))) {
        score += 3;
        reasons.push({ label: toTitleCase(answers.note), points: 3 });
      } else if (midNotes.some(n => n.includes(note) || note.includes(n))) {
        score += 2.5;
        reasons.push({ label: toTitleCase(answers.note), points: 2.5 });
      } else if (baseNotes.some(n => n.includes(note) || note.includes(n))) {
        score += 2;
        reasons.push({ label: toTitleCase(answers.note), points: 2 });
      }
    }

    // ── Vibe → occasions + mood ──
    const vibeValues = answers.vibe || [];
    let allOccasions = [];
    let allMoods     = [];
    vibeValues.forEach(v => {
      const mapped = VIBE_MAP[v];
      if (mapped) {
        allOccasions = allOccasions.concat(mapped.occasions);
        allMoods     = allMoods.concat(mapped.moods);
      }
    });

    // Occasions
    const pOccasions     = product.occasion || [];
    const pOccasionsNorm = pOccasions.map(o => normalizeStr(o));
    if (pOccasions.length === 0) {
      score += 0.5;
    } else {
      const uniqueOccasions = [...new Set(allOccasions)];
      uniqueOccasions.forEach(oc => {
        const mappedOc    = OCCASION_VALUE_MAP[oc] || oc;
        const secondaryOc = oc === 'date-night' ? 'special-occasion' : null;
        if (pOccasionsNorm.includes(mappedOc) || (secondaryOc && pOccasionsNorm.includes(secondaryOc))) {
          score += 2;
          reasons.push({ label: OCCASION_LABELS[oc] || toTitleCase(oc), points: 2 });
        }
      });
    }

    // Mood (via quiz_projection field)
    if (allMoods.length > 0) {
      const pProjection = normalizeStr(product.quiz_projection);
      for (const mood of allMoods) {
        const expected = MOOD_TO_PROJECTION_MAP[mood];
        if (expected && pProjection === expected) {
          score += 1.5;
          reasons.push({ label: 'Mood match', points: 1.5 });
          break; // award once
        }
      }
    }

    // ── Feel → intensity + sillage + longevity ──
    const feelData = FEEL_MAP[answers.feel];
    if (feelData) {
      const pIntensity = normalizeStr(product.scent_intensity);
      if (feelData.intensities && feelData.intensities.includes(pIntensity)) {
        score += 2;
        reasons.push({ label: toTitleCase(answers.feel) + ' intensity', points: 2 });
      }

      const pSillage = normalizeStr(product.quiz_sillage);
      if (feelData.sillage && pSillage === normalizeStr(feelData.sillage)) {
        score += 2;
      }

      const pLongevity = normalizeStr(product.longevity || '');
      if (feelData.longevity && pLongevity && pLongevity === feelData.longevity) {
        score += 1.5;
        reasons.push({ label: toTitleCase(feelData.longevity) + ' wear', points: 1.5 });
      }
    }

    // Top reasons by point value, deduplicated
    reasons.sort((a, b) => b.points - a.points);
    const topReasons = [...new Map(reasons.map(r => [r.label, r])).values()]
      .slice(0, 5)
      .map(r => r.label);

    const pct = Math.round((score / maxScore) * 100);
    return { score, pct, topReasons };
  }

  function runScoring() {
    const answers  = state.answers;
    const maxScore = computeMaxScore(answers);
    const products = state.products || [];

    const scored = products.map(product => {
      const { score, pct, topReasons } = scoreProduct(product, answers, maxScore);
      return { ...product, _score: score, _pct: pct, _reasons: topReasons };
    });

    scored.sort((a, b) => b._score - a._score);

    const tier1 = scored.filter(p => p._pct >= 80);
    const tier2 = scored.filter(p => p._pct >= 60 && p._pct < 80);
    const tier3 = scored.filter(p => p._pct >= 40 && p._pct < 60);

    // Carousel: tier1 first, fill with tier2, cap at 15
    let carousel = [...tier1, ...tier2];
    if (carousel.length < 8) {
      carousel = [...carousel, ...tier3.slice(0, 8 - carousel.length)];
    }
    carousel = carousel.slice(0, 15);

    const carouselIds = new Set(carousel.map(p => p.id));
    const tier3Extra  = tier3.filter(p => !carouselIds.has(p.id));

    state.results = { carousel, tier3Extra };
    lsSet(LS.RESULTS, state.results);
    return state.results;
  }

  /* ──────────────────────────────────────────────────────────
     FINISH QUIZ → RESULTS DIRECTLY (no email gate)
  ────────────────────────────────────────────────────────── */
  function finishQuiz() {
    trackEvent('quiz_completed', {
      gender:         state.answers.gender,
      scent_families: (state.answers.scentFamilies || []).join(','),
      vibe:           (state.answers.vibe || []).join(','),
    });

    // Complete progress
    if (DOM.progressbar) DOM.progressbar.setAttribute('aria-valuenow', STEPS.length.toString());
    if (DOM.progressFill) DOM.progressFill.style.width = '100%';

    // Show brief loading animation while scoring runs
    DOM.stepsContainer.innerHTML = [
      '<div class="quiz-loading" aria-label="Finding your matches">',
        '<div class="quiz-loading-dots" role="status"><span></span><span></span><span></span></div>',
        '<p class="quiz-loading__text">Finding your matches&hellip;</p>',
      '</div>',
    ].join('');

    // Run scoring then show results
    const scoringDone = new Promise(resolve => {
      setTimeout(() => { runScoring(); resolve(); }, 50);
    });
    const minDelay = new Promise(resolve => setTimeout(resolve, 900));

    Promise.all([scoringDone, minDelay]).then(() => {
      DOM.stepsContainer.innerHTML = '';
      showResultsPhase();
    });
  }

  /* ──────────────────────────────────────────────────────────
     RESULTS PHASE
  ────────────────────────────────────────────────────────── */
  function showResultsPhase() {
    state.phase = 'results';
    if (DOM.emailWrapper) DOM.emailWrapper.classList.add('hidden');
    if (!state.results) runScoring();

    const { carousel, tier3Extra } = state.results;

    DOM.resultsContainer.classList.remove('hidden');
    DOM.resultsContainer.innerHTML = buildResultsHTML(carousel, tier3Extra);

    equalizeCarouselDescriptions();

    bindResultsEvents();
    initCarousel();
    scrollToQuiz();

    trackEvent('quiz_results_shown', {
      match_count: carousel.length,
      tier1_count: carousel.filter(p => p._pct >= 80).length,
    });

    // Show soft email prompt after delay if not already captured
    const savedEmail = lsGet(LS.EMAIL);
    if (!savedEmail) {
      setTimeout(showEmailPrompt, 1200);
    }
  }

  /* ──────────────────────────────────────────────────────────
     POST-RESULTS EMAIL PROMPT (non-blocking)
  ────────────────────────────────────────────────────────── */
  function showEmailPrompt() {
    if ($('quiz-email-post-prompt')) return; // already shown

    const prompt = document.createElement('div');
    prompt.id        = 'quiz-email-post-prompt';
    prompt.className = 'quiz-email-post-prompt';
    prompt.setAttribute('aria-label', 'Save your fragrance matches');

    prompt.innerHTML = [
      '<button type="button" class="quiz-email-post-prompt__close" id="quiz-email-prompt-close" aria-label="Dismiss">&times;</button>',
      '<div class="quiz-email-post-prompt__content">',
        '<p class="quiz-email-post-prompt__headline">Save your matches</p>',
        '<p class="quiz-email-post-prompt__sub">Enter your email to revisit your results any time.</p>',
        '<form class="quiz-email-post-form" id="quiz-email-post-form" novalidate>',
          '<div class="quiz-email-post-prompt__row">',
            '<input type="email" id="quiz-email-post-input" name="email" class="field__input" placeholder="Email address" autocomplete="email" aria-required="true" aria-label="Email address">',
            '<button type="submit" class="button button--primary button--black quiz-email-post-submit">Save</button>',
          '</div>',
          '<p class="quiz-email-capture__privacy">No spam. Unsubscribe any time.</p>',
          '<button type="button" class="quiz-email-skip-link" id="quiz-email-post-skip">No thanks</button>',
        '</form>',
      '</div>',
    ].join('');

    // Insert after results header, before carousel
    const header = DOM.resultsContainer.querySelector('.quiz-results__header');
    if (header) header.insertAdjacentElement('afterend', prompt);
    else DOM.resultsContainer.insertBefore(prompt, DOM.resultsContainer.firstChild);

    // Trigger entrance animation
    requestAnimationFrame(() => prompt.classList.add('is-visible'));

    const closeBtn = $('quiz-email-prompt-close');
    const skipBtn  = $('quiz-email-post-skip');
    const form     = $('quiz-email-post-form');

    function dismissPrompt() {
      prompt.classList.remove('is-visible');
      setTimeout(() => prompt.remove(), 300);
    }

    if (closeBtn) closeBtn.addEventListener('click', dismissPrompt);
    if (skipBtn)  skipBtn.addEventListener('click', dismissPrompt);

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = $('quiz-email-post-input');
        const email = (input ? input.value : '').trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

        try { await subscribeToKlaviyo(email); } catch (err) { console.warn('Klaviyo:', err); }

        lsSet(LS.EMAIL, email);
        trackEvent('quiz_email_captured', { email_captured: true });

        prompt.innerHTML = '<p class="quiz-email-post-prompt__success">Saved! Check your inbox.</p>';
        setTimeout(() => dismissPrompt(), 2000);
      });
    }
  }

  /* ──────────────────────────────────────────────────────────
     RESULTS HTML BUILDER
  ────────────────────────────────────────────────────────── */
  function buildResultsHTML(carousel, tier3Extra) {
    return [
      '<div class="quiz-results__header">',
        '<p class="quiz-results__count">Showing ' + carousel.length + ' matches for your profile</p>',
        '<div class="quiz-results__actions">',
          '<button class="button quiz-retake-btn" id="quiz-retake-btn">Retake Quiz</button>',
          '<button class="button quiz-share-btn" id="quiz-share-btn">Share Results</button>',
          '<span class="quiz-share-confirm" id="quiz-share-confirm">Link copied!</span>',
        '</div>',
      '</div>',

      '<div class="quiz-carousel-wrapper">',
        '<div class="quiz-carousel" id="quiz-carousel" aria-label="Fragrance match results">',
          '<div class="quiz-carousel__track" id="quiz-carousel-track">',
            carousel.map(p => buildProductCardHTML(p)).join(''),
          '</div>',
        '</div>',
        '<button class="quiz-carousel__nav quiz-carousel__prev" id="quiz-carousel-prev" aria-label="Previous fragrance">',
          buildChevronSVG('left'),
        '</button>',
        '<button class="quiz-carousel__nav quiz-carousel__next" id="quiz-carousel-next" aria-label="Next fragrance">',
          buildChevronSVG('right'),
        '</button>',
      '</div>',

      tier3Extra.length > 0 ? buildTier3HTML(tier3Extra) : '',
    ].join('');
  }

  function buildChevronSVG(dir) {
    const path = dir === 'left'
      ? '<polyline points="15 18 9 12 15 6"></polyline>'
      : '<polyline points="9 18 15 12 9 6"></polyline>';
    return '<svg class="quiz-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">' + path + '</svg>';
  }

  /* ── Product result card (updated with accord bars + new fields) ── */
  function buildProductCardHTML(product) {
    const imageSrc   = product.image || '';
    const imageAlt   = escapeHTML(product.image_alt || product.title || '');
    const title      = escapeHTML(product.title || '');
    const subtitle   = escapeHTML(product.subtitle || product.inspired_by || '');
    const desc       = escapeHTML(product.fragrance_description || '');
    const price      = formatPrice(product.price);
    const pct        = product._pct || 0;
    const handle     = product.handle || '';
    const variantId  = product.default_variant_id || '';
    const dayNight   = product.day_night || '';
    const longevity  = normalizeStr(product.longevity || '');

    // Accord bars from accord_weights
    const accordWeights = product.accord_weights || {};
    const topAccords    = Object.entries(accordWeights)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);


    const accordBarsHTML = topAccords.length > 0
      ? '<div class="quiz-accord-bars">' +
          topAccords.map(([key, val]) =>
            '<div class="quiz-accord-bar">' +
              '<span class="quiz-accord-bar__label">' + escapeHTML(toTitleCase(key.replace(/-/g, ' '))) + '</span>' +
              '<div class="quiz-accord-bar__track">' +
                '<div class="quiz-accord-bar__fill" style="width:' + Math.min(Math.round(val), 100) + '%"></div>' +
              '</div>' +
            '</div>'
          ).join('') +
        '</div>'
      : '';

    // Fallback scent pills if no accord_weights
    const scents     = normalizeList(product.scent).slice(0, 2);
    const scentPills = !topAccords.length && scents.length
      ? '<div class="quiz-scent-pills">' +
          scents.map(s => '<span class="quiz-scent-pill">' + escapeHTML(toTitleCase(s.replace(/-/g, ' '))) + '</span>').join('') +
        '</div>'
      : '';

    // Day/night tag
    const dayNightLabel = dayNight === 'day' ? 'Day wear' : dayNight === 'night' ? 'Evening' : dayNight === 'both' ? 'Versatile' : '';
    const dayNightHTML  = dayNightLabel
      ? '<span class="quiz-day-night-tag">' + escapeHTML(dayNightLabel) + '</span>'
      : '';

    // Longevity indicator (1–4 bars)
    const longevityLevels = { poor: 1, moderate: 2, 'long-lasting': 3, eternal: 4 };
    const longevityLevel  = longevityLevels[longevity] || 0;
    const longevityHTML   = longevityLevel > 0
      ? '<div class="quiz-longevity" aria-label="Longevity: ' + escapeHTML(toTitleCase(longevity)) + '">' +
          [1, 2, 3, 4].map(n =>
            '<span class="quiz-longevity__bar' + (n <= longevityLevel ? ' is-filled' : '') + '"></span>'
          ).join('') +
          '<span class="quiz-longevity__label">' + escapeHTML(longevity === 'long-lasting' ? 'Long lasting' : toTitleCase(longevity)) + '</span>' +
        '</div>'
      : '';

    return [
      '<div class="quiz-result-card product-card-wrapper">',
        '<div class="quiz-card-image-wrap">',
          imageSrc
            ? '<img src="' + escapeHTML(imageSrc) + '" alt="' + imageAlt + '" loading="lazy" width="600" height="750">'
            : '<div class="quiz-card-image-placeholder"></div>',
          '<div class="quiz-match-badge">' + pct + '% Match</div>',
        '</div>',
        '<div class="quiz-card-body">',
          '<div class="quiz-card-top">',
            dayNightHTML,
            '<h3 class="quiz-card-title">' + title + '</h3>',
            subtitle ? '<p class="quiz-inspired-by">' + subtitle + '</p>' : '',
            desc    ? '<p class="quiz-card-description">' + desc + '</p>' : '',
          '</div>',
          '<div class="quiz-card-bottom">',
            accordBarsHTML,
            scentPills,
            longevityHTML,
            price ? '<p class="quiz-card-price">' + escapeHTML(price) + '</p>' : '',
            '<div class="quiz-card-actions">',
              '<button ',
                'class="button button--primary button--black button--full-width quiz-atc-btn" ',
                'data-product-id="' + escapeHTML(String(product.id)) + '" ',
                'data-variant-id="' + escapeHTML(String(variantId)) + '" ',
                'aria-label="Add ' + title + ' to cart"',
              '>',
                'Add to Cart',
              '</button>',
              handle
                ? '<a href="/products/' + escapeHTML(handle) + '" class="quiz-full-details-link" aria-label="View full details for ' + title + '">Full details &rarr;</a>'
                : '',
            '</div>',
          '</div>',
        '</div>',
      '</div>',
    ].join('');
  }

  function buildTier3HTML(products) {
    return [
      '<div class="quiz-tier3-accordion" id="quiz-tier3-accordion">',
        '<button class="quiz-tier3-toggle" id="quiz-tier3-toggle" aria-expanded="false">',
          'Also worth exploring (' + products.length + ' more)',
          '<svg class="quiz-tier3-icon" viewBox="0 0 24 24" aria-hidden="true">',
            '<polyline points="6 9 12 15 18 9"></polyline>',
          '</svg>',
        '</button>',
        '<div class="quiz-tier3-body" id="quiz-tier3-body" role="region" aria-labelledby="quiz-tier3-toggle">',
          '<div class="quiz-tier3-grid">',
            products.map(p => buildProductCardHTML(p)).join(''),
          '</div>',
        '</div>',
      '</div>',
    ].join('');
  }

  /* ──────────────────────────────────────────────────────────
     RESULTS EVENTS
  ────────────────────────────────────────────────────────── */
  function bindResultsEvents() {
    if (DOM.resultsContainer._eventsBound) return;
    DOM.resultsContainer._eventsBound = true;

    const retakeBtn = $('quiz-retake-btn');
    if (retakeBtn) retakeBtn.addEventListener('click', retakeQuiz);

    const shareBtn     = $('quiz-share-btn');
    const shareConfirm = $('quiz-share-confirm');
    if (shareBtn) {
      shareBtn.addEventListener('click', () => {
        const url = getShareableURL();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => {
            if (shareConfirm) {
              shareConfirm.classList.add('is-visible');
              setTimeout(() => shareConfirm.classList.remove('is-visible'), 2500);
            }
          });
        } else {
          window.prompt('Copy this link:', url);
        }
        trackEvent('quiz_share', { method: 'url' });
      });
    }

    const tier3Toggle = $('quiz-tier3-toggle');
    const tier3Accord = $('quiz-tier3-accordion');
    if (tier3Toggle && tier3Accord) {
      tier3Toggle.addEventListener('click', () => {
        const isOpen = tier3Accord.classList.toggle('is-open');
        tier3Toggle.setAttribute('aria-expanded', String(isOpen));
      });
    }

    DOM.resultsContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.quiz-atc-btn');
      if (!btn) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      handleAddToCart(btn, variantId);
    });
  }

  /* ──────────────────────────────────────────────────────────
     ADD TO CART
  ────────────────────────────────────────────────────────── */
  function handleAddToCart(btn, variantId) {
    btn.classList.add('is-loading');
    btn.disabled = true;

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(res => {
        if (!res.ok) throw new Error('Cart error');
        return res.json();
      })
      .then(() => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = 'Added!';
        trackEvent('quiz_add_to_cart', { variant_id: variantId });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.textContent = 'Add to Cart';
          btn.disabled    = false;
        }, 2500);
      })
      .catch(() => {
        btn.classList.remove('is-loading');
        btn.textContent = 'Error \u2013 retry';
        btn.disabled    = false;
        setTimeout(() => { btn.textContent = 'Add to Cart'; }, 2000);
      });
  }

  /* ──────────────────────────────────────────────────────────
     CARD DESCRIPTION EQUALIZER
     Sets min-height on all carousel descriptions to the tallest
     so accord bars line up across cards regardless of text length.
  ────────────────────────────────────────────────────────── */
  function equalizeCarouselDescriptions() {
    const descs = DOM.resultsContainer.querySelectorAll(
      '.quiz-carousel__track .quiz-card-description'
    );
    if (!descs.length) return;
    descs.forEach(el => { el.style.minHeight = ''; });
    let maxH = 0;
    descs.forEach(el => { maxH = Math.max(maxH, el.offsetHeight); });
    descs.forEach(el => { el.style.minHeight = maxH + 'px'; });
  }

  /* ──────────────────────────────────────────────────────────
     CAROUSEL
  ────────────────────────────────────────────────────────── */
  function initCarousel() {
    const carousel = $('quiz-carousel');
    const track    = $('quiz-carousel-track');
    const prevBtn  = $('quiz-carousel-prev');
    const nextBtn  = $('quiz-carousel-next');

    if (!carousel || !track) return;

    let currentIndex = 0;
    const cards      = track.querySelectorAll('.quiz-result-card');
    if (!cards.length) return;

    function getVisibleCount() {
      return window.innerWidth >= 750 ? 3 : 1;
    }

    function getCardWidth() {
      const firstCard = cards[0];
      if (!firstCard) return 0;
      const gap = parseInt(getComputedStyle(track).gap, 10) || 20;
      return firstCard.offsetWidth + gap;
    }

    function updateCarousel(animate) {
      const cardW  = getCardWidth();
      const offset = currentIndex * cardW;
      track.style.transition = animate === false ? 'none' : '';
      track.style.transform  = 'translateX(-' + offset + 'px)';

      const visible = getVisibleCount();
      const maxIdx  = Math.max(0, cards.length - visible);

      if (prevBtn) prevBtn.disabled = currentIndex <= 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= maxIdx;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (currentIndex > 0) { currentIndex--; updateCarousel(); }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const maxIdx = Math.max(0, cards.length - getVisibleCount());
        if (currentIndex < maxIdx) { currentIndex++; updateCarousel(); }
      });
    }

    carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { if (currentIndex > 0) { currentIndex--; updateCarousel(); } }
      if (e.key === 'ArrowRight') {
        const maxIdx = Math.max(0, cards.length - getVisibleCount());
        if (currentIndex < maxIdx) { currentIndex++; updateCarousel(); }
      }
    });

    let touchStartX = 0;
    let touchStartY = 0;
    let isDragging  = false;

    carousel.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    carousel.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) < Math.abs(dy) || Math.abs(dx) < 30) return;
      const maxIdx = Math.max(0, cards.length - getVisibleCount());
      if (dx < 0 && currentIndex < maxIdx) { currentIndex++; updateCarousel(); }
      if (dx > 0 && currentIndex > 0)      { currentIndex--; updateCarousel(); }
    }, { passive: true });

    let mouseStartX = 0;
    carousel.addEventListener('mousedown', (e) => {
      isDragging  = true;
      mouseStartX = e.clientX;
      carousel.classList.add('is-dragging');
    });
    window.addEventListener('mousemove', () => {});
    window.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      carousel.classList.remove('is-dragging');
      const dx     = e.clientX - mouseStartX;
      const maxIdx = Math.max(0, cards.length - getVisibleCount());
      if (dx < -50 && currentIndex < maxIdx) { currentIndex++; updateCarousel(); }
      if (dx > 50  && currentIndex > 0)      { currentIndex--; updateCarousel(); }
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { updateCarousel(false); }, 200);
    });

    updateCarousel(false);
  }

  /* ──────────────────────────────────────────────────────────
     EMAIL CAPTURE — KLAVIYO
  ────────────────────────────────────────────────────────── */
  async function subscribeToKlaviyo(email) {
    const topFamilies = (state.answers.scentFamilies || [])
      .map(f => FAMILY_LABELS[f] || f)
      .join(', ');

    const payload = {
      token:    KLAVIYO_PK,
      list_id:  KLAVIYO_LIST_ID,
      profiles: [{
        email:              email,
        quiz_completed:     true,
        quiz_top_families:  topFamilies,
        quiz_gender:        state.answers.gender || '',
        quiz_feel:          state.answers.feel || '',
        quiz_note:          state.answers.note || '',
        quiz_vibe:          (state.answers.vibe || []).join(', '),
        quiz_variant:       state.variant || 'A',
      }],
    };

    const response = await fetch(
      'https://a.klaviyo.com/api/v2/list/' + KLAVIYO_LIST_ID + '/subscribe?api_key=' + KLAVIYO_PK,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      }
    );

    if (!response.ok && response.status !== 200) {
      throw new Error('Klaviyo error: ' + response.status);
    }
    return response;
  }

  /* ──────────────────────────────────────────────────────────
     RETAKE QUIZ
  ────────────────────────────────────────────────────────── */
  function retakeQuiz() {
    state.answers      = {};
    state.skippedSteps = new Set();
    state.results      = null;
    state.currentStep  = 0;
    state.phase        = 'quiz';

    lsRemove(LS.ANSWERS);
    lsRemove(LS.RESULTS);

    DOM.resultsContainer.classList.add('hidden');
    DOM.resultsContainer.innerHTML   = '';
    DOM.resultsContainer._eventsBound = false;
    if (DOM.emailWrapper) DOM.emailWrapper.classList.add('hidden');

    if (DOM.progressFill)  DOM.progressFill.style.width = '0%';
    if (DOM.progressLabel) DOM.progressLabel.textContent = 'Step 1 of ' + STEPS.length;
    if (DOM.progressbar)   DOM.progressbar.setAttribute('aria-valuenow', '0');

    // Reset dots
    document.querySelectorAll('.quiz-progress__dot').forEach(dot => {
      dot.classList.remove('is-active', 'is-completed');
    });

    renderStep(0);
    trackEvent('quiz_retake');
  }

  /* ──────────────────────────────────────────────────────────
     RESUME PROMPT
  ────────────────────────────────────────────────────────── */
  function checkResumePrompt() {
    const saved = lsGet(LS.ANSWERS);
    if (!saved || !saved.answers || !saved.answers.gender) return false;

    const prompt = $('quiz-resume-prompt');
    if (!prompt) return false;

    prompt.classList.remove('hidden');

    const resumeBtn = $('quiz-resume-btn');
    const freshBtn  = $('quiz-fresh-btn');

    if (resumeBtn) {
      resumeBtn.addEventListener('click', () => {
        prompt.classList.add('hidden');
        state.answers      = saved.answers;
        state.skippedSteps = new Set(saved.skippedSteps || []);
        const resumeIndex  = findResumeStep();
        state.currentStep  = resumeIndex;
        renderStep(resumeIndex);
        trackEvent('quiz_resumed');
      });
    }

    if (freshBtn) {
      freshBtn.addEventListener('click', () => {
        prompt.classList.add('hidden');
        lsRemove(LS.ANSWERS);
        lsRemove(LS.RESULTS);
        renderStep(0);
        trackEvent('quiz_started_fresh');
      });
    }

    return true;
  }

  function findResumeStep() {
    for (let i = 0; i < STEPS.length; i++) {
      const step = STEPS[i];
      const ans  = state.answers[step.key];
      if (step.skippable && state.skippedSteps.has(step.id)) continue;
      if (!ans || (Array.isArray(ans) && ans.length === 0)) return i;
    }
    return STEPS.length - 1;
  }

  /* ──────────────────────────────────────────────────────────
     SHARED URL → AUTO-POPULATE
  ────────────────────────────────────────────────────────── */
  function checkSharedURL() {
    const decoded = decodeURLToAnswers();
    if (!decoded) return false;

    state.answers      = decoded.answers;
    state.skippedSteps = decoded.skipped;

    lsSet(LS.ANSWERS, { answers: state.answers, skippedSteps: [...state.skippedSteps] });
    return true;
  }

  /* ──────────────────────────────────────────────────────────
     PRODUCT DATA LOADING
  ────────────────────────────────────────────────────────── */
  async function loadProducts() {
    const url = (window.AMARA_QUIZ && window.AMARA_QUIZ.productsUrl) || '';
    if (!url) {
      console.warn('Fragrance Quiz: no products URL configured.');
      return [];
    }

    try {
      const res  = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      return Array.isArray(data) ? data : (data.products || []);
    } catch (err) {
      console.warn('Fragrance Quiz: failed to load quiz-products.json', err);
      return [];
    }
  }

  /* ──────────────────────────────────────────────────────────
     INIT
  ────────────────────────────────────────────────────────── */
  async function init() {
    DOM.coverPanel       = $('quiz-cover');
    DOM.progressFill     = $('quiz-progress-fill');
    DOM.progressLabel    = $('quiz-progress-label');
    DOM.progressbar      = $('quiz-progressbar');
    DOM.stepsContainer   = $('quiz-steps-container');
    DOM.emailWrapper     = $('quiz-email-wrapper');
    DOM.resultsContainer = $('quiz-results');

    if (!DOM.stepsContainer) return;

    initABTest();

    loadProducts().then(products => { state.products = products; });

    // 1. Shared URL → jump straight to results, skip cover
    if (checkSharedURL()) {
      if (DOM.coverPanel) DOM.coverPanel.classList.add('hidden');
      if (DOM.progressbar) DOM.progressbar.classList.remove('hidden');
      DOM.stepsContainer.innerHTML = [
        '<div class="quiz-loading" aria-label="Loading your results">',
          '<div class="quiz-loading-dots"><span></span><span></span><span></span></div>',
          '<p class="quiz-loading__text">Loading your results&hellip;</p>',
        '</div>',
      ].join('');
      updateProgress(STEPS.length - 1);

      const waitForProducts = () => new Promise(resolve => {
        if (state.products !== null) { resolve(); return; }
        const check = setInterval(() => {
          if (state.products !== null) { clearInterval(check); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(check); resolve(); }, 5000);
      });
      await waitForProducts();
      DOM.stepsContainer.innerHTML = '';
      runScoring();
      showResultsPhase();
      trackEvent('quiz_shared_url_load');
      return;
    }

    // 2. Cover hero → defer quiz start until button click
    if (DOM.coverPanel) {
      const coverBtn = $('quiz-cover-btn');
      if (coverBtn) {
        coverBtn.addEventListener('click', () => {
          DOM.coverPanel.classList.add('hidden');
          if (DOM.progressbar) DOM.progressbar.classList.remove('hidden');
          // Check for saved progress only after cover is dismissed
          const hasResume = checkResumePrompt();
          if (!hasResume) {
            renderStep(0);
            trackEvent('quiz_started');
          }
        });
      }
    } else {
      const hasResume = checkResumePrompt();
      if (!hasResume) {
        renderStep(0);
        trackEvent('quiz_started');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
